import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Tooltip } from '@repo/design-system/components/ds/tooltip/tooltip';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectMapConfig, selectMapMetadata, selectMapName, selectPlayerLocationDeltas, selectSteamId, selectTitleId, selectTitleName } from '@repo/store/maps/slices/mapData';
import type { GameSessionInternal, SessionOwnerResponse } from '@repo/types/sessions/session-types';
import { useAuth } from '@repo/ui-web/auth/auth-hooks';
import { aggregateInternalGameEvents, isGameEvent } from '@repo/utility/events';
import { getSteamImageUrl, getUserAvatarUrl } from '@repo/utility/image-urls';
import { getSessionOwner } from '@repo/wemod-api-client/sessions-api-client';
import * as MapLibre from 'maplibre-gl';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSessionTrailPlayback } from '../game-map/hooks/useSessionTrailPlayback';
import { useMapContext } from '../game-map/map-context';
import { EVENT_CATEGORY_COLORS } from '../game-map/renderers/trail-rendering';
import { transformEventCoordinates, transformTrailCoordinates } from '../game-map/utils/coordinate-transform';
import { SessionShareMenu } from '../map-sidebar/session-share-menu';
import { generateAndDownloadShareImage } from '../share/share-image-generator';
const MAP_IDLE_TIMEOUT_MS = 2000;
const SLIDER_PRECISION = 10000;
interface SessionTimelineProps {
  session: GameSessionInternal;
  visible?: boolean;
  position?: 'top' | 'bottom';
  onSharingChange?: (sessionUuid: string, isShared: boolean) => void;
}
export const SessionTimeline: React.FC<SessionTimelineProps> = ({
  session,
  visible = false,
  position = 'bottom',
  onSharingChange
}) => {
  const sessionInitializedRef = useRef<string | null>(null);
  const [isGeneratingDownload, setIsGeneratingDownload] = useState(false);
  const playbackProgressRef = useRef(0);
  const {
    map
  } = useMapContext();
  const {
    user
  } = useAuth();
  const {
    t
  } = useT(['game_map']);
  const analytics = useAnalytics();
  const mapName = useAppSelector(selectMapName);
  const titleId = useAppSelector(selectTitleId);
  const titleName = useAppSelector(selectTitleName) || t($ => $.unknown_game);
  const steamId = useAppSelector(selectSteamId);
  const playerLocationDeltas = useAppSelector(selectPlayerLocationDeltas);
  const mapMetadata = useAppSelector(selectMapMetadata);
  const mapConfig = useAppSelector(selectMapConfig);
  const isPublicSession = !!mapConfig?.publicSessionId;
  const [sessionOwner, setSessionOwner] = useState<SessionOwnerResponse | null>(null);
  useEffect(() => {
    if (!isPublicSession || !session?.uuid) return;
    let cancelled = false;
    getSessionOwner(session.uuid).then(owner => {
      if (!cancelled) setSessionOwner(owner);
    }).catch(err => {
      console.error('Failed to fetch session owner:', err);
    });
    return () => {
      cancelled = true;
    };
  }, [isPublicSession, session?.uuid]);
  const playback = useSessionTrailPlayback(visible ? session : null, {
    visible,
    targetRecapSeconds: 5,
    markers: {
      start: true,
      finish: true,
      current: false,
      head: false
    },
    autoFitBounds: true
  });
  playbackProgressRef.current = playback.progress;
  const trailStart = session?.trail?.[0]?.timestamp ?? 0;
  const trailEnd = session?.trail?.length ? session.trail[session.trail.length - 1]?.timestamp ?? 0 : 0;
  const totalDuration = trailEnd - trailStart;
  const eventTicks = useMemo(() => {
    if (!session?.events?.length || totalDuration <= 0) return [];
    return session.events.filter(e => isGameEvent(e.eventType)).map(e_0 => {
      const progress = Math.max(0, Math.min(1, (e_0.timestamp - trailStart) / totalDuration));
      return {
        position: progress * 100,
        progress,
        color: EVENT_CATEGORY_COLORS[e_0.eventCategory] || EVENT_CATEGORY_COLORS.general,
        name: e_0.eventName
      };
    });
  }, [session?.events, trailStart, totalDuration]);
  useEffect(() => {
    if (session && visible && totalDuration > 0) {
      const sessionKey = `${session.uuid}-${visible}`;
      if (sessionInitializedRef.current !== sessionKey) {
        sessionInitializedRef.current = sessionKey;
        playback.jumpToEnd();
      }
    } else if (!session || !visible) {
      sessionInitializedRef.current = null;
      playback.clear();
    }
  }, [session, totalDuration, visible, playback.jumpToEnd, playback.clear]);
  useEffect(() => {
    if (!map || !playback.isPlaying) return;
    const handleMapClick = () => {
      playback.pause();
    };
    map.on('click', handleMapClick);
    map.on('contextmenu', handleMapClick);
    return () => {
      map.off('click', handleMapClick);
      map.off('contextmenu', handleMapClick);
    };
  }, [map, playback.isPlaying, playback.pause]);
  const handlePlayPause = () => {
    playback.isPlaying ? playback.pause() : playback.play();
  };
  const handleReset = () => {
    playback.restart();
  };
  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newProgress = parseInt(event.target.value, 10) / SLIDER_PRECISION;
    playback.setProgress(newProgress);
  };
  const handleDownload = useCallback(async () => {
    if (!map || !session || !session.trail.length || isGeneratingDownload) return;
    analytics?.event('game_map_session_timeline_download_click', {
      sessionUuid: session.uuid,
      titleId: titleId ?? '',
      titleName,
      mapName: mapName ?? ''
    });
    setIsGeneratingDownload(true);
    const currentProgress = playbackProgressRef.current;
    try {
      if (currentProgress < 1.0) {
        playback.setProgress(1.0);
        await new Promise<void>(resolve => {
          let resolved = false;
          const onIdle = () => {
            if (!resolved) {
              resolved = true;
              clearTimeout(timeoutId);
              resolve();
            }
          };
          const timeoutId = setTimeout(() => {
            if (!resolved) {
              resolved = true;
              map.off('idle', onIdle);
              resolve();
            }
          }, MAP_IDLE_TIMEOUT_MS);
          map.once('idle', onIdle);
          map.triggerRepaint();
        });
      }
      const allCoordinates = playerLocationDeltas ? transformTrailCoordinates(session, playerLocationDeltas) : session.trail.map(point => [point.x, point.y] as [number, number]);
      if (allCoordinates.length === 0) {
        throw new Error('No trail coordinates available');
      }
      const bounds_0 = allCoordinates.reduce((bounds, coord) => {
        return bounds.extend(coord);
      }, new MapLibre.LngLatBounds(allCoordinates[0], allCoordinates[0]));
      const gameCapsuleUrl = steamId ? getSteamImageUrl(steamId, 'capsule') : '';
      const username = isPublicSession && sessionOwner ? sessionOwner.username : user?.username || t($_1 => $_1.session_history.share_player_fallback);
      const userAvatarUrl = isPublicSession && sessionOwner ? getUserAvatarUrl(sessionOwner.profileImageUrl, {
        source: 'maps-share-image'
      }) : getUserAvatarUrl(user?.profileImage, {
        source: 'maps-share-image'
      });
      const events = session.events?.length ? transformEventCoordinates(session.events, session.trail, playerLocationDeltas ?? null) : undefined;
      const aggregatedEvents = session.events?.length ? aggregateInternalGameEvents(session.events) : undefined;
      await generateAndDownloadShareImage(map, {
        gameName: titleName,
        gameCapsuleUrl,
        username,
        userAvatarUrl,
        sessionDate: new Date(session.startedAt),
        trailBounds: bounds_0,
        trailCoordinates: allCoordinates,
        maxZoom: mapMetadata?.maxZoom ? mapMetadata.maxZoom - 1 : undefined,
        events,
        aggregatedEvents
      }, {
        wasPlaying: t($_2 => $_2.session_history.share_was_playing),
        yourFavorite: t($_3 => $_3.session_history.share_your_favorite),
        gamesAt: t($_4 => $_4.session_history.share_games_at)
      });
      toast({
        description: t($_5 => $_5.session_history.screenshot_captured),
        variant: 'success'
      });
    } catch (error) {
      console.error('Failed to generate share image:', error);
      toast({
        description: t($_0 => $_0.session_history.screenshot_failed),
        variant: 'error'
      });
    } finally {
      if (currentProgress < 1.0) {
        playback.setProgress(currentProgress);
      }
      setIsGeneratingDownload(false);
    }
  }, [analytics, map, mapName, session, playerLocationDeltas, steamId, titleId, user, titleName, t, playback.setProgress, mapMetadata, isGeneratingDownload, isPublicSession, sessionOwner]);
  if (!visible || !session) return null;
  return <div className={css({
    position: 'absolute',
    left: '[50%]',
    bottom: position === 'bottom' ? '16' : '[auto]',
    top: position === 'top' ? '16' : '[auto]',
    transform: 'translateX(-50%)',
    zIndex: 10,
    background: 'blackAlpha.40',
    borderRadius: '16',
    boxShadow: 'xl',
    padding: '16',
    width: '[90%]',
    maxWidth: '[600px]',
    backdropFilter: '[blur(25px)]'
  })}>
            <FlexRow gap="12" align="center" width="full">
                <FlexRow gap="6">
                    <Button variant="filled" onClick={handleReset} color="secondary" size="sm" disabled={playback.progress === 0} title={t($_6 => $_6.session_history.reset_to_start)} className={css({
          minHeight: '[32px]'
        })}>
                        <Icon icon="replay" decorative />
                    </Button>

                    <Button variant="filled" onClick={handlePlayPause} color="white" size="sm" title={playback.isPlaying ? t($_7 => $_7.session_history.pause) : t($_8 => $_8.session_history.play)} className={css({
          minHeight: '[32px]'
        })}>
                        <Icon fill icon={playback.isPlaying ? 'pause' : 'play_arrow'} decorative />
                    </Button>
                </FlexRow>

                <div className={css({
        flex: '1',
        position: 'relative',
        height: '[32px]',
        width: 'full',
        borderRadius: '8',
        overflow: 'hidden'
      })}>
                    <input type="range" min="0" max={SLIDER_PRECISION} value={Math.round(playback.progress * SLIDER_PRECISION)} onChange={handleSliderChange} className={css({
          width: 'full',
          height: '[32px]',
          borderRadius: '8',
          background: 'transparent',
          outline: 'none',
          cursor: 'pointer',
          appearance: 'none',
          position: 'relative',
          '&::-webkit-slider-track': {
            height: '[32px]',
            borderRadius: '8',
            background: 'transparent'
          },
          '&::-webkit-slider-thumb': {
            appearance: 'none',
            height: '[32px]',
            width: '[6px]',
            borderRadius: '8',
            background: 'white',
            cursor: 'pointer',
            border: 'none',
            position: 'relative',
            zIndex: 2
          },
          '&::-moz-range-track': {
            height: '[32px]',
            borderRadius: '8',
            background: 'transparent',
            border: 'none'
          },
          '&::-moz-range-thumb': {
            height: '[32px]',
            width: '[6px]',
            borderRadius: '8',
            background: 'white',
            cursor: 'pointer',
            border: 'none'
          }
        })} />

                    <div className={css({
          position: 'absolute',
          top: '0',
          left: '0',
          width: 'full',
          height: '[32px]',
          borderRadius: '8',
          background: 'whiteAlpha.10',
          pointerEvents: 'none',
          zIndex: 1
        })}>
                        <div className={css({
            height: 'full',
            borderRadius: '8',
            background: 'white',
            transition: playback.isPlaying ? '[none]' : '[width 0.2s ease]'
          })} style={{
            width: `${playback.progress * 100}%`,
            borderTopRightRadius: playback.progress >= 1 ? undefined : 0,
            borderBottomRightRadius: playback.progress >= 1 ? undefined : 0
          }} />
                    </div>

                    {eventTicks.map((tick, index) => <Tooltip key={`${tick.position}-${index}`} content={tick.name} size="sm" placement="top" openDelay={0} closeDelay={0} contentClassName={css({
          paddingX: '6'
        })}>
                            <button type="button" onClick={() => playback.setProgress(tick.progress)} onKeyDown={e_1 => {
            if (e_1.key === 'Enter') {
              playback.setProgress(tick.progress);
            }
          }} className={css({
            position: 'absolute',
            top: '0',
            width: '[8px]',
            marginLeft: '-2',
            height: '[32px]',
            zIndex: 3,
            borderRadius: '2',
            cursor: 'pointer',
            background: 'transparent',
            border: 'none',
            padding: '0',
            _hover: {
              opacity: 1,
              '& > div': {
                width: '[6px]'
              }
            }
          })} style={{
            left: `${tick.position}%`
          }}>
                                <div className={css({
              width: '[4px]',
              height: 'full',
              borderRadius: '2',
              opacity: 0.8,
              marginX: 'auto',
              transition: '[width 0.1s ease]'
            })} style={{
              backgroundColor: tick.color
            }} />
                            </button>
                        </Tooltip>)}
                </div>

                <Type variant="body-s" color="text.primary" className={css({
        fontFamily: 'mono'
      })}>
                    {playback.formattedCurrentTime}/{playback.formattedTotalTime}
                </Type>

                <div className={css({
        width: '[1px]',
        height: '[32px]',
        background: 'whiteAlpha.15'
      })} />

                <FlexRow gap="4">
                    <Button variant="filled" onClick={handleDownload} color="primary" size="sm" disabled={isGeneratingDownload} title={t($_9 => $_9.session_history.share_trail_screenshot)} className={css({
          minHeight: '[32px]'
        })}>
                        <Icon icon={isGeneratingDownload ? 'hourglass_empty' : 'arrow_downward'} decorative />
                        <span className={css({
            mdDown: {
              display: 'none'
            }
          })}>{t($_10 => $_10.session_history.save)}</span>
                    </Button>

                    {!isPublicSession && session.hasFirestoreData !== false && <SessionShareMenu session={session} onSharingChange={onSharingChange} iconOnly={false} placement="top-end" trigger="map-session-timeline" />}
                </FlexRow>
            </FlexRow>
        </div>;
};